import { useEffect, useId, useRef } from 'react'
import { useLang } from '../i18n'
import type { AgentScopeAsk, AgentScopeChoice, ChatScope } from '../lib/api'
import { LOCK_ASK_TIMEOUT_MS } from './LockAskCard'

const SCOPE_LABEL: Record<ChatScope, 'scope_part' | 'scope_artefact' | 'scope_bid'> = {
  part: 'scope_part',
  artefact: 'scope_artefact',
  bid: 'scope_bid',
}

/**
 * A paused run's question about the chat target (AB#337): the request needs more than the selected
 * target, so the agent asks to widen it — Widen to <wider target> / Stay on <current target>. Same
 * channel and pattern as {@link LockAskCard}: one answer per question, and past the deadline the
 * card answers Stay by itself, exactly once.
 */
export function ScopeAskCard({ ask, onAnswer }: { ask: AgentScopeAsk; onAnswer: (choice: AgentScopeChoice) => void }) {
  const { t } = useLang()
  const titleId = useId()
  const answered = useRef(false)
  const onAnswerRef = useRef(onAnswer)
  onAnswerRef.current = onAnswer

  const answer = (choice: AgentScopeChoice): void => {
    if (answered.current) return
    answered.current = true
    onAnswerRef.current(choice)
  }

  // One timer per question: at its deadline (capped at the ask timeout), Stay.
  useEffect(() => {
    answered.current = false
    const left = Date.parse(ask.expiresAt) - Date.now()
    const ms = Number.isFinite(left) ? Math.min(Math.max(0, left), LOCK_ASK_TIMEOUT_MS) : LOCK_ASK_TIMEOUT_MS
    const h = setTimeout(() => answer('stay'), ms)
    return () => clearTimeout(h)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-armed per question, not per render
  }, [ask.id])

  const from = t(SCOPE_LABEL[ask.from])
  const to = t(SCOPE_LABEL[ask.to])

  return (
    <section className="chat-lock-ask" aria-labelledby={titleId}>
      <p id={titleId} className="chat-lock-ask-title">{t('scope_ask_title').replace('{to}', () => to)}</p>
      <p>{t('scope_ask_lead').replace('{from}', () => from)}</p>
      {ask.reason && <p className="chat-lock-ask-target">{ask.reason}</p>}
      <div className="chat-lock-ask-actions">
        <button type="button" className="mini no" onClick={() => answer('stay')}>
          {t('scope_ask_stay').replace('{from}', () => from)}
        </button>
        <button type="button" className="mini ok" onClick={() => answer('widen')}>
          {t('scope_ask_widen').replace('{to}', () => to)}
        </button>
      </div>
      <p className="chat-lock-ask-hint">{t('scope_ask_timeout')}</p>
    </section>
  )
}
